// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {buildVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import {setRoomVideo} from '@app/features/voice_scene/commands/VoiceSeatCommands';
import styles from '@app/features/voice_scene/components/VoiceSceneTv.module.css';
import VoiceSceneTvRequest from '@app/features/voice_scene/state/VoiceSceneTvRequest';
import {parseYouTubeVideoId} from '@fluxer/constants/src/VoiceRoomWatch';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {YoutubeLogoIcon} from '@phosphor-icons/react';
import type React from 'react';
import {useState} from 'react';

const PLAY_A_VIDEO_DESCRIPTOR = msg({
	message: 'Play a video',
	comment:
		'3D voice room TV: tooltip and accessible name of the button under the TV that opens a box for a YouTube link to play on the TV for everyone in the room.',
});
const VIDEO_LINK_DESCRIPTOR = msg({
	message: 'Video link',
	comment: '3D voice room TV: label of the box where a YouTube link is pasted to play it on the TV for everyone.',
});
const PLAY_ON_TV_DESCRIPTOR = msg({
	message: 'Play on TV',
	comment: '3D voice room TV: button that starts the pasted YouTube link on the TV for everyone in the room.',
});
const NOT_A_VIDEO_DESCRIPTOR = msg({
	message: 'That is not a YouTube video link.',
	comment:
		'3D voice room TV: inline warning under the video link box when the pasted text is not a YouTube video link.',
});
const NOT_PLAYED_DESCRIPTOR = msg({
	message: 'The video could not be played. Try again.',
	comment: '3D voice room TV: inline warning under the video link box when the server did not start the video.',
});

const LINK_MAX_LENGTH = 2048;

// Only a YouTube video link goes on: checked here first, then again on the server, which keeps only its id. Once it
// plays, this viewer's TV turns to it, even over a screen share.
export function VoiceSceneTvLinkForm({channelId, onDone}: {channelId: string; onDone: () => void}) {
	const {i18n} = useLingui();
	const [value, setValue] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [sending, setSending] = useState(false);
	const submit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (sending) return;
		const videoId = parseYouTubeVideoId(value);
		if (!videoId) {
			setError(i18n._(NOT_A_VIDEO_DESCRIPTOR));
			return;
		}
		setSending(true);
		const connectionId = await setRoomVideo(channelId, {videoId, positionMs: 0, paused: false});
		setSending(false);
		if (!connectionId) {
			setError(i18n._(NOT_PLAYED_DESCRIPTOR));
			return;
		}
		const me = Authentication.currentUserId;
		if (me)
			VoiceSceneTvRequest.select(channelId, {kind: 'link', identity: buildVoiceParticipantIdentity(me, connectionId)});
		onDone();
	};
	return (
		<form
			className={styles.linkForm}
			onSubmit={submit}
			noValidate
			data-flx="voice-scene.voice-scene-tv-link-box.voice-scene-tv-link-form.link-form.submit"
		>
			<Input
				label={i18n._(VIDEO_LINK_DESCRIPTOR)}
				value={value}
				onChange={(event) => {
					setValue(event.target.value);
					setError(null);
				}}
				placeholder="https://www.youtube.com/watch?v=…"
				error={error}
				maxLength={LINK_MAX_LENGTH}
				inputMode="url"
				autoComplete="off"
				spellCheck={false}
				autoFocus
				data-flx="voice-scene.voice-scene-tv-link-box.voice-scene-tv-link-form.input.set-value"
			/>
			<Button
				type="submit"
				small
				submitting={sending}
				data-flx="voice-scene.voice-scene-tv-link-box.voice-scene-tv-link-form.button.submit"
			>
				{i18n._(PLAY_ON_TV_DESCRIPTOR)}
			</Button>
		</form>
	);
}

export function VoiceSceneTvLinkBox({channel}: {channel: Channel}) {
	const {i18n} = useLingui();
	const label = i18n._(PLAY_A_VIDEO_DESCRIPTOR);
	return (
		<Popout
			position="top"
			tooltip={label}
			render={({onClose}) => (
				<VoiceSceneTvLinkForm
					channelId={channel.id}
					onDone={onClose}
					data-flx="voice-scene.voice-scene-tv-link-box.voice-scene-tv-link-form"
				/>
			)}
			data-flx="voice-scene.voice-scene-tv-link-box.popout"
		>
			<Button
				small
				square
				variant="inverted"
				icon={
					<YoutubeLogoIcon weight="fill" size={16} data-flx="voice-scene.voice-scene-tv-link-box.youtube-logo-icon" />
				}
				aria-label={label}
				data-flx="voice-scene.voice-scene-tv-link-box.button"
			/>
		</Popout>
	);
}
